"use client"

import { FormikProvider, type FormikErrors, useFormik } from "formik"

import { FormSelect } from "@/components/form-inputs"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  useMarkDeliveryCrmHandoffVerifiedMutation,
  useRetryDeliveryCrmHandoffMutation,
} from "@/hooks/mutations/delivery-shipments"
import { DELIVERY_CRM_UNKNOWN_ERROR_CODE } from "@/lib/deliveries/crm-handoff-constants"
import type { DeliveryShipmentListItem } from "@/server-actions/delivery-shipments"
import { useTranslations } from "next-intl"


type RecoveryVerification = "not_checked" | "note_exists" | "note_absent"

type RecoveryFormValues = {
  verification: RecoveryVerification
}

const initialValues: RecoveryFormValues = { verification: "not_checked" }
function getverificationOptions(tUi: ReturnType<typeof useTranslations<"Deliveries.DeliveriesRecoverDeliveryCrmHandoffDialog">>) {
  return [
  { value: "note_exists", label: tUi("theWorkerNoteExistsAndTheCRMStatus") },
  { value: "note_absent", label: tUi("theWorkerNoteIsAbsent") },
] as const
}

export function RecoverDeliveryCrmHandoffDialog({
  shipment,
  onOpenChange,
}: {
  shipment: DeliveryShipmentListItem | null
  onOpenChange: (open: boolean) => void
}) {
  const tUi = useTranslations("Deliveries.DeliveriesRecoverDeliveryCrmHandoffDialog")
  const verificationOptions = getverificationOptions(tUi)

  const tCommonUi = useTranslations("Common")

  const retryMutation = useRetryDeliveryCrmHandoffMutation()
  const verifiedMutation = useMarkDeliveryCrmHandoffVerifiedMutation()
  const requiresVerification = shipment?.crmLastErrorCode
    === DELIVERY_CRM_UNKNOWN_ERROR_CODE
  const formik = useFormik<RecoveryFormValues>({
    initialValues,
    enableReinitialize: true,
    validate: values => validateRecovery(values, requiresVerification),
    onSubmit: async (values, helpers) => {
      if (!shipment) return
      try {
        switch (values.verification) {
          case "note_exists":
            await verifiedMutation.mutateAsync({
              shipmentId: shipment.id,
              confirmedNoteExists: true,
            })
            break
          case "note_absent":
            await retryMutation.mutateAsync({
              shipmentId: shipment.id,
              confirmedNoteAbsent: true,
            })
            break
          case "not_checked":
            await retryMutation.mutateAsync({ shipmentId: shipment.id })
            break
          default: {
            const exhaustiveVerification: never = values.verification
            throw new Error(
              `Unsupported CRM recovery choice: ${exhaustiveVerification}`,
            )
          }
        }
        helpers.resetForm()
        onOpenChange(false)
      } catch {
        // withQueryHandling has already surfaced the safe server error.
      }
    },
  })

  return (
    <Dialog open={shipment !== null} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle><span key={tUi("recoverCRMWorkerHandoff")}>{tUi("recoverCRMWorkerHandoff")}</span></DialogTitle>
          <DialogDescription>
            <span key={tUi("thisOnlyRepairsTheCRMNotificationItNever")}>{tUi("thisOnlyRepairsTheCRMNotificationItNever")}</span></DialogDescription>
        </DialogHeader>
        {requiresVerification ? (
          <Alert>
            <AlertTitle><span key={tUi("checkTheCRMBeforeRetrying")}>{tUi("checkTheCRMBeforeRetrying")}</span></AlertTitle>
            <AlertDescription>
              <span><span key={tUi("thePreviousCRMResponseWasInconclusiveSearchThe")}>{tUi("thePreviousCRMResponseWasInconclusiveSearchThe")}</span></span> <span className="font-mono text-xs">
                {shipment?.crmFailureNoteMarker ?? (shipment ? `[versafy-delivery-shipment:${shipment.id}]` : "")}
              </span><span><span key={tUi("recordingAnExistingNoteAvoidsADuplicateRetry")}>{tUi("recordingAnExistingNoteAvoidsADuplicateRetry")}</span></span>
            </AlertDescription>
          </Alert>
        ) : (
          <Alert>
            <AlertTitle><span key={tUi("theWaybillWillNotBeRecreated")}>{tUi("theWaybillWillNotBeRecreated")}</span></AlertTitle>
            <AlertDescription>
              <span key={tUi("versafyWillRetryTheCRMHandoffTheStructured")}>{tUi("versafyWillRetryTheCRMHandoffTheStructured")}</span></AlertDescription>
          </Alert>
        )}
        <FormikProvider value={formik}>
          <form onSubmit={formik.handleSubmit} className="space-y-4">
            {requiresVerification ? (
              <FormSelect
                name="verification"
                label={tUi("cRMVerificationResult")}
                options={verificationOptions}
                placeholder={tUi("selectWhatYouFound")}
              />
            ) : null}
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
                disabled={formik.isSubmitting}
              >
                <span key={tCommonUi("cancel")}>{tCommonUi("cancel")}</span></Button>
              <Button type="submit" disabled={formik.isSubmitting || !shipment}>
                <span key={formik.isSubmitting ? tUi("processing") : tUi("continue")}>{formik.isSubmitting ? tUi("processing") : tUi("continue")}</span>
              </Button>
            </DialogFooter>
          </form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  )
}

function validateRecovery(
  values: RecoveryFormValues,
  requiresVerification: boolean,
): FormikErrors<RecoveryFormValues> {
  if (requiresVerification && values.verification === "not_checked") {
    return { verification: "Select what you found in the CRM." }
  }
  return {}
}
